import { z } from 'zod';
import { CoverageReasonSchema } from './workflow.js';
import { REVIEW_AREA_LABELS, preSiteOrderAction } from './review-language.js';
import { RefreshStateSchema } from './refresh.js';
export const COVERAGE_REASON_LABELS = {
    assessment_missing: 'Pre-site review not found',
    configuration_changed: 'Configuration changed since review',
    measurements_changed: 'Measurements changed since review',
    freshness_unverified: 'Form revision unverified',
    python_findings_unresolved: 'Pre-site findings unresolved',
    scope_uncertain: 'Pre-site review scope uncertain',
};
export const QueueViewSchema = z.enum(['current', 'mine', 'history', 'all']);
export const QUEUE_VIEWS = {
    current: 'Current collection',
    mine: 'My work',
    history: 'Historical reviews',
    all: 'All reviews',
};
export const QueueCollectionSchema = z.object({
    id: z.string().uuid(),
    report_start_date: z.string(),
    report_end_date: z.string(),
    timezone: z.string(),
    project_count: z.number(),
    unconfirmed_project_count: z.number(),
});
/** Shared presentation vocabulary; it never changes a model judgment or task. */
export const ReviewCategorySchema = z.enum([
    'document_conflict',
    'missing_evidence',
    'clarification',
    'stage1_follow_up',
    'processing_issue',
    'follow_up',
    'processing',
    'verified',
    'resolved',
    'reviewed',
]);
export const REVIEW_CATEGORIES = {
    document_conflict: { label: 'Document conflict', color: 'red' },
    missing_evidence: { label: 'Missing evidence', color: 'yellow' },
    clarification: { label: 'Needs clarification', color: 'yellow' },
    stage1_follow_up: { label: 'Pre-site follow-up', color: 'yellow' },
    processing_issue: { label: 'Processing issue', color: 'orange' },
    follow_up: { label: 'Reviewer follow-up', color: 'yellow' },
    processing: { label: 'Processing', color: 'blue' },
    verified: { label: 'Verified', color: 'green' },
    resolved: { label: 'Closed follow-up', color: 'blue' },
    reviewed: { label: 'Human review complete', color: 'blue' },
};
export function comparisonCategory(check) {
    if (check.outcome === 'match' || check.attention === 'information')
        return 'follow_up';
    if (check.outcome === 'discrepancy' &&
        (check.attention === 'action_required' || check.attention == null))
        return 'document_conflict';
    if (check.issue_kind === 'missing_document' ||
        check.issue_kind === 'approval_gap')
        return 'missing_evidence';
    if (check.issue_kind === 'stage1_evidence')
        return 'stage1_follow_up';
    return 'clarification';
}
export function projectReviewCategory(project) {
    if (project.state === 'failed')
        return 'processing_issue';
    if (!project.is_current || !project.last_result)
        return project.state ? 'processing' : 'missing_evidence';
    if (project.resolved)
        return 'resolved';
    const orders = project.last_result.workflow?.orders ?? [];
    const checks = orders
        .flatMap((order) => order.stages.flatMap((stage) => stage.checks))
        .filter((check) => check.outcome !== 'match' &&
        check.attention !== 'information' &&
        check.applicability !== 'not_applicable');
    const categories = checks.map(comparisonCategory);
    if (checks.length && project.open_task_count === 0)
        return 'follow_up';
    if (categories.includes('document_conflict'))
        return 'document_conflict';
    if (orders.some((order) => !['covered', 'not_applicable'].includes(order.measurement.state)))
        return 'stage1_follow_up';
    if (categories.includes('missing_evidence') ||
        project.last_result.review_status === 'Waiting on documents' ||
        orders.some((order) => order.stages.some((stage) => stage.state === 'awaiting_documents')))
        return 'missing_evidence';
    if (checks.length ||
        project.last_result.review_status === 'Needs clarification')
        return 'clarification';
    if (project.open_task_count)
        return 'follow_up';
    if (orders.length &&
        orders.every((order) => order.verification === 'verified'))
        return 'verified';
    return 'follow_up';
}
export const QueueStageSchema = z.enum([
    'pre_site',
    'po',
    'acknowledgement',
    'measurement_applicability',
    'collection',
    'analysis',
]);
export const QUEUE_STAGE_LABELS = {
    ...REVIEW_AREA_LABELS,
    collection: 'Evidence collection',
    analysis: 'Analysis',
};
export const ReviewQueueQuerySchema = z.object({
    view: QueueViewSchema.default('current'),
    search: z.string().max(100).default(''),
    state: z.enum(['active', 'all', 'resolved']).default('active'),
    owner: z.enum(['all', 'mine', 'unassigned']).default('all'),
    stage: z
        .enum([
        'all',
        'pre_site',
        'measurement_applicability',
        'po',
        'acknowledgement',
        'collection',
        'analysis',
    ])
        .default('all'),
    category: z.union([z.literal('all'), ReviewCategorySchema]).default('all'),
    coverage_reason: z
        .union([z.literal('all'), CoverageReasonSchema])
        .default('all'),
    offset: z.coerce.number().int().min(0).default(0),
    limit: z.coerce.number().int().min(1).max(50).default(50),
});
export const ReviewQueueAlertSchema = z.object({
    id: z.string(),
    source: z.enum(['pre_site', 'ordering']),
    project_id: z.string().nullable(),
    display_number: z.string().nullable(),
    project_available: z.boolean(),
    job_id: z.string().nullable(),
    order_ref: z.string().nullable(),
    stage: QueueStageSchema,
    field: z.string(),
    category: ReviewCategorySchema,
    title: z.string(),
    summary: z.string(),
    brief: z.string().default(''),
    coverage_reasons: z.array(CoverageReasonSchema).default([]),
    action: z.string(),
    href: z.string(),
    state: z.string(),
    severity: z.string(),
    assigned_user_id: z.string().nullable(),
    owner_name: z.string().nullable(),
    responsible_role: z.string(),
    due_at: z.string().nullable(),
    created_at: z.string(),
    // Same issue in multiple comparison directions retains its stages for filtering.
    stages: z.array(QueueStageSchema),
});
export function queueActionLabel(alert) {
    switch (alert.stage) {
        case 'pre_site':
            return 'View pre-site report';
        case 'measurement_applicability':
            return preSiteOrderAction(alert.coverage_reasons);
        case 'po':
        case 'acknowledgement':
            return 'Compare documents';
        case 'collection':
            return 'Review missing evidence';
        case 'analysis':
            return 'Review analysis';
    }
}
export const ReviewQueueSchema = z.object({
    refresh: RefreshStateSchema.optional(),
    view: QueueViewSchema,
    current_collection: QueueCollectionSchema.nullable(),
    view_counts: z.object({
        current: z.number(),
        mine: z.number(),
        history: z.number(),
        all: z.number(),
    }),
    items: z.array(z.object({
        key: z.string(),
        project_id: z.string().nullable(),
        display_number: z.string().nullable(),
        project_available: z.boolean(),
        job_ids: z.array(z.string()),
        top_alert: ReviewQueueAlertSchema,
        alerts: z.array(ReviewQueueAlertSchema),
        alert_count: z.number(),
        collection_membership: z.enum(['current', 'earlier', 'unconfirmed']),
    })),
    total: z.number(),
    project_count: z.number(),
    unlinked_job_count: z.number(),
    unconfirmed_count: z.number(),
    category_counts: z.record(z.string(), z.number()),
});
